/* @img-migrated */
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Upload, Globe, Phone, Mail, MapPin, Camera, AlertTriangle,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Save, Eye, Trash2, CheckCircle2, Info,
  Gift, RotateCcw, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { useAutoDraft } from '../../hooks/useAutoDraft';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

import { useAuth } from '../../context/AuthContext';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalCompanyProfile = () => {
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState({
    company_name: '', logo_url: '', industry: '', description: '', vision: '', mission: '',
    slogan: '', website: '', phone: '', email: '', address: '',
    social_facebook: '', social_instagram: '', social_linkedin: '', social_twitter: '', social_youtube: '',
    contact_person: '', gallery_images: [], promo_video_url: '',
    offers_member_discount: false, discount_description: '', discount_category: '', discount_contact_method: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  // --- Auto-draft + unsaved-changes ---
  const { restored, clearDraft } = useAutoDraft('dccf_draft_company_profile', profile, { enabled: !loading });
  const dirty = useMemo(() => {
    if (loading || !initialSnapshot) return false;
    try { return JSON.stringify(profile) !== JSON.stringify(initialSnapshot); } catch { return false; }
  }, [profile, initialSnapshot, loading]);
  useUnsavedChanges(dirty);

  useEffect(() => {
    fetchProfile();
     
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/portal/profile`, { withCredentials: true });
      if (res.data) {
        const loaded = { ...res.data };
        setProfile(prev => ({ ...prev, ...loaded }));
        setInitialSnapshot({ ...{
          company_name: '', logo_url: '', industry: '', description: '', vision: '', mission: '',
          slogan: '', website: '', phone: '', email: '', address: '',
          social_facebook: '', social_instagram: '', social_linkedin: '', social_twitter: '', social_youtube: '',
          contact_person: '', gallery_images: [], promo_video_url: '',
          offers_member_discount: false, discount_description: '', discount_category: '', discount_contact_method: '',
        }, ...loaded });
        calculateCompletion(res.data);
      }
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const calculateCompletion = (data) => {
    const fields = ['company_name', 'logo_url', 'industry', 'description', 'website', 'phone', 'email', 'address', 'vision', 'mission'];
    const filled = fields.filter(f => data[f]).length;
    setProfileCompletion(Math.round((filled / fields.length) * 100));
  };

  const restoreDraftToForm = () => {
    if (restored?.data) {
      setProfile(restored.data);
      toast.success('Draft restored');
    }
  };

  const handleSave = async () => {
    if (!profile.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/portal/profile`, profile, { withCredentials: true });
      toast.success('Profile saved successfully');
      if (res.data.profile_completion !== undefined) {
        setProfileCompletion(res.data.profile_completion);
      }
      setInitialSnapshot({ ...profile });
      clearDraft();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save — your changes are auto-saved, try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('png')) {
      toast.error('Please upload a PNG file for the best quality. Transparent background preferred.', { duration: 5000 });
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/portal/profile/logo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded!');
      refreshUser();
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/portal/profile/gallery`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(prev => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), res.data.image_url],
      }));
      toast.success('Image added to gallery!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const inputClass = "w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]";

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-5 lg:p-8" data-testid="portal-company-profile">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl lg:text-2xl font-bold text-[#002249]">Company Profile</h1>
        </div>
        <p className="text-sm text-slate-600 mt-2">Manage your official business information and branding assets.</p>
      </motion.div>

      {/* Public directory sync notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="public-directory-notice">
        <p className="text-[13px] text-blue-800 leading-relaxed">
          <span className="font-semibold">Public Directory Sync:</span> This information powers your listing in <a href="/members" target="_blank" className="underline font-semibold">Our Business Community</a> on the DCCF website. Changes saved here will update your public business profile automatically.
        </p>
      </div>

      {/* Draft restore banner (auto-save) */}
      {restored?.data && initialSnapshot &&
        JSON.stringify(restored.data) !== JSON.stringify(initialSnapshot) &&
        JSON.stringify(restored.data) !== JSON.stringify(profile) && (
        <div
          className="mb-4 p-3 rounded-lg bg-[#002249]/5 border border-[#002249]/15 flex items-center justify-between gap-3"
          data-testid="profile-draft-restore-banner"
        >
          <span className="text-[13px] text-[#002249] font-medium flex items-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Unsaved changes detected from your last session.
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => clearDraft()} className="h-8" data-testid="profile-discard-draft">Discard</Button>
            <Button size="sm" onClick={restoreDraftToForm} className="h-8 bg-[#002249]" data-testid="profile-restore-draft">Restore</Button>
          </div>
        </div>
      )}

      {/* Profile Completion */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="profile-completion"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-[#002249]">Profile Completion</span>
          <span className="text-sm font-bold text-[#002249]">{profileCompletion}%</span>
        </div>
        <Progress value={profileCompletion} className="h-2" />
        {profileCompletion < 100 && (
          <p className="text-xs text-slate-500 mt-2">Complete your profile to maximize visibility in the member directory.</p>
        )}
      </motion.div>

      {/* Logo Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="logo-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Company Logo</h2>

        {/* Logo Upload Rules */}
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4" data-testid="logo-upload-rules">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 text-sm mb-1">Logo Upload Requirements</h3>
              <ul className="text-xs text-amber-700 space-y-1">
                <li className="flex items-center"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-amber-600" /> <strong>PNG format ONLY</strong> for best quality</li>
                <li className="flex items-center"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-amber-600" /> <strong>High resolution</strong> (minimum 500x500px recommended)</li>
                <li className="flex items-center"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-amber-600" /> <strong>Transparent background</strong> preferred</li>
                <li className="flex items-center"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-amber-600" /> <strong>Clean, centered</strong> logo image</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-6">
          <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden">
            {profile.logo_url ? (
              <Img variant="business"
                src={resolveImg(profile.logo_url)}
                alt="Company logo"
                className="w-full h-full object-contain p-2"
              />
            ) : (
              <Building2 className="w-12 h-12 text-slate-300" />
            )}
          </div>
          <div>
            <label className="inline-flex items-center px-4 py-2 bg-[#002249] text-white rounded-sm cursor-pointer hover:bg-[#001630] text-sm font-medium" data-testid="upload-logo-btn">
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Logo (PNG)'}
              <input type="file" accept="image/png,image/*" onChange={handleLogoUpload} className="hidden" />
            </label>
            <p className="text-xs text-slate-500 mt-2">PNG with transparent background recommended</p>
          </div>
        </div>
      </motion.div>

      {/* Company Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="company-info-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Company Information</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Company Name <span className="text-[#E63946]">*</span>
            </label>
            <input type="text" value={profile.company_name} onChange={e => setProfile({ ...profile, company_name: e.target.value })} className={inputClass} data-testid="profile-company-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry</label>
            <select value={profile.industry || ''} onChange={e => setProfile({ ...profile, industry: e.target.value })} className={inputClass} data-testid="profile-industry">
              <option value="">Select Industry</option>
              <option value="restaurants">Restaurants & Hospitality</option>
              <option value="professional">Professional Services</option>
              <option value="health">Health & Wellness</option>
              <option value="real_estate">Real Estate</option>
              <option value="finance">Finance</option>
              <option value="marketing">Marketing & Media</option>
              <option value="technology">Technology</option>
              <option value="education">Education</option>
              <option value="retail">Retail</option>
              <option value="community">Community Organizations</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Description</label>
            <textarea value={profile.description || ''} onChange={e => setProfile({ ...profile, description: e.target.value })} placeholder="Describe your business..." className={`${inputClass} min-h-[100px]`} data-testid="profile-description" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Slogan</label>
            <input type="text" value={profile.slogan || ''} onChange={e => setProfile({ ...profile, slogan: e.target.value })} placeholder="Your business slogan" className={inputClass} data-testid="profile-slogan" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Person</label>
            <input type="text" value={profile.contact_person || ''} onChange={e => setProfile({ ...profile, contact_person: e.target.value })} placeholder="Primary contact name" className={inputClass} data-testid="profile-contact-person" />
          </div>
        </div>
      </motion.div>

      {/* Vision & Mission */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="vision-mission-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Vision & Mission</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Vision Statement</label>
            <textarea value={profile.vision || ''} onChange={e => setProfile({ ...profile, vision: e.target.value })} placeholder="What does your company aspire to achieve?" className={`${inputClass} min-h-[80px]`} data-testid="profile-vision" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Mission Statement</label>
            <textarea value={profile.mission || ''} onChange={e => setProfile({ ...profile, mission: e.target.value })} placeholder="What is your company's purpose and core values?" className={`${inputClass} min-h-[80px]`} data-testid="profile-mission" />
          </div>
        </div>
      </motion.div>

      {/* Contact Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="contact-info-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Contact Information</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Globe className="w-3.5 h-3.5 mr-1.5" /> Website</label>
            <input type="url" value={profile.website || ''} onChange={e => setProfile({ ...profile, website: e.target.value })} placeholder="https://yourcompany.com" className={inputClass} data-testid="profile-website" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Phone className="w-3.5 h-3.5 mr-1.5" /> Phone</label>
            <input type="tel" value={profile.phone || ''} onChange={e => setProfile({ ...profile, phone: e.target.value })} placeholder="+1 (xxx) xxx-xxxx" className={inputClass} data-testid="profile-phone" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Mail className="w-3.5 h-3.5 mr-1.5" /> Email</label>
            <input type="email" value={profile.email || ''} onChange={e => setProfile({ ...profile, email: e.target.value })} placeholder="info@yourcompany.com" className={inputClass} data-testid="profile-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><MapPin className="w-3.5 h-3.5 mr-1.5" /> Address</label>
            <input type="text" value={profile.address || ''} onChange={e => setProfile({ ...profile, address: e.target.value })} placeholder="Business address" className={inputClass} data-testid="profile-address" />
          </div>
        </div>
      </motion.div>

      {/* Social Media Links */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="social-links-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Social Media Links</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Facebook className="w-3.5 h-3.5 mr-1.5 text-blue-600" /> Facebook</label>
            <input type="url" value={profile.social_facebook || ''} onChange={e => setProfile({ ...profile, social_facebook: e.target.value })} placeholder="https://facebook.com/yourpage" className={inputClass} data-testid="profile-facebook" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Instagram className="w-3.5 h-3.5 mr-1.5 text-pink-600" /> Instagram</label>
            <input type="url" value={profile.social_instagram || ''} onChange={e => setProfile({ ...profile, social_instagram: e.target.value })} placeholder="https://instagram.com/yourhandle" className={inputClass} data-testid="profile-instagram" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Linkedin className="w-3.5 h-3.5 mr-1.5 text-blue-700" /> LinkedIn</label>
            <input type="url" value={profile.social_linkedin || ''} onChange={e => setProfile({ ...profile, social_linkedin: e.target.value })} placeholder="https://linkedin.com/company/..." className={inputClass} data-testid="profile-linkedin" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Twitter className="w-3.5 h-3.5 mr-1.5 text-sky-500" /> Twitter / X</label>
            <input type="url" value={profile.social_twitter || ''} onChange={e => setProfile({ ...profile, social_twitter: e.target.value })} placeholder="https://twitter.com/yourhandle" className={inputClass} data-testid="profile-twitter" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center"><Youtube className="w-3.5 h-3.5 mr-1.5 text-red-600" /> YouTube</label>
            <input type="url" value={profile.social_youtube || ''} onChange={e => setProfile({ ...profile, social_youtube: e.target.value })} placeholder="https://youtube.com/c/..." className={inputClass} data-testid="profile-youtube" />
          </div>
        </div>
      </motion.div>

      {/* Gallery */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-6"
        data-testid="gallery-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Photo Gallery</h2>
        <label className="inline-flex items-center px-4 py-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 text-sm mb-4" data-testid="upload-gallery-btn">
          <Camera className="w-4 h-4 mr-2 text-slate-500" />
          {uploading ? 'Uploading...' : 'Add Photos'}
          <input type="file" accept="image/*" onChange={handleGalleryUpload} className="hidden" />
        </label>
        {profile.gallery_images?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {profile.gallery_images.map((img, i) => (
              <div key={i} className="relative group aspect-square">
                <Img variant="generic"
                  src={resolveImg(img)}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-full object-cover rounded-sm border"
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No gallery images yet. Add photos to showcase your business.</p>
        )}
      </motion.div>

      {/* Promo Video */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="bg-white border border-slate-200 rounded-sm p-5 mb-8"
        data-testid="promo-video-section"
      >
        <h2 className="text-lg font-bold text-[#002249] mb-4">Promotional Video</h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Video URL (YouTube, Vimeo, etc.)</label>
          <input type="url" value={profile.promo_video_url || ''} onChange={e => setProfile({ ...profile, promo_video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." className={inputClass} data-testid="profile-promo-video" />
        </div>
      </motion.div>

      {/* Member Benefits — Feb 2026 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="bg-white rounded-sm shadow-sm p-6 border border-slate-200"
        data-testid="member-benefits-section"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#002249]">Member Benefits</h2>
              <p className="text-[13px] text-slate-500 mt-0.5 max-w-lg">
                Offer a discount or perk to fellow DCCF members. Enabled listings appear in the Member Deals directory with a badge.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer flex-shrink-0" data-testid="benefits-toggle-label">
            <span className="text-[13px] font-medium text-slate-700 hidden sm:inline">
              {profile.offers_member_discount ? 'Enabled' : 'Disabled'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={!!profile.offers_member_discount}
              onClick={() => setProfile({ ...profile, offers_member_discount: !profile.offers_member_discount })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${profile.offers_member_discount ? 'bg-emerald-600' : 'bg-slate-300'}`}
              data-testid="benefits-toggle"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${profile.offers_member_discount ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </label>
        </div>

        {profile.offers_member_discount && (
          <div className="space-y-4 pl-0 sm:pl-13">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Discount description <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={profile.discount_description || ''}
                onChange={e => setProfile({ ...profile, discount_description: e.target.value })}
                rows={3}
                placeholder="e.g. 15% off all services for DCCF members. Valid through Dec 2026."
                className={`${inputClass} min-h-[80px]`}
                data-testid="benefits-description"
                maxLength={400}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Shown to other members. Keep it clear, specific, and easy to redeem.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
                <select
                  value={profile.discount_category || ''}
                  onChange={e => setProfile({ ...profile, discount_category: e.target.value })}
                  className={inputClass}
                  data-testid="benefits-category"
                >
                  <option value="">Select category...</option>
                  <option>Professional Services</option>
                  <option>Food & Restaurants</option>
                  <option>Retail</option>
                  <option>Health & Wellness</option>
                  <option>Beauty & Personal Care</option>
                  <option>Home & Services</option>
                  <option>Legal & Financial</option>
                  <option>Marketing & Creative</option>
                  <option>Technology</option>
                  <option>Travel & Hospitality</option>
                  <option>Education & Training</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">How to redeem</label>
                <input
                  type="text"
                  value={profile.discount_contact_method || ''}
                  onChange={e => setProfile({ ...profile, discount_contact_method: e.target.value })}
                  placeholder='e.g. "Mention DCCF at checkout" / "Call to book"'
                  className={inputClass}
                  data-testid="benefits-redeem"
                  maxLength={160}
                />
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Email Preferences shortcut — compact link */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="mt-6 bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm text-[#1a1a1a]">Email Preferences</div>
            <div className="text-xs text-slate-500 truncate">Pick which Chamber emails you want to receive.</div>
          </div>
        </div>
        <a
          href="/portal/email-preferences"
          className="text-sm font-semibold text-[#002249] hover:underline flex-shrink-0"
          data-testid="go-to-email-prefs"
        >
          Manage →
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.47 }}
        className="mt-3 bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm text-[#1a1a1a]">Account Settings</div>
            <div className="text-xs text-slate-500 truncate">Change the email you sign in with.</div>
          </div>
        </div>
        <a
          href="/portal/account"
          className="text-sm font-semibold text-[#002249] hover:underline flex-shrink-0"
          data-testid="go-to-account-settings"
        >
          Manage →
        </a>
      </motion.div>

      {/* Save Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="sticky bottom-0 bg-slate-50/95 backdrop-blur-sm p-4 -mx-6 lg:-mx-8 px-6 lg:px-8 border-t border-slate-200"
      >
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-[#002249] hover:bg-[#001630] rounded-sm px-8" data-testid="save-profile-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default PortalCompanyProfile;
